import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';

import { StartTrialModal, TrialExpiredModal } from '../../dashboard/shared';
import { START_TRIAL } from '../../actions/actionTypes';
import { usePlan, PLAN } from './utils';
import LoadingAndPulse from '../shared/LoadingAndPulse';

type Modes = 'fullComponent' | 'onlyFunctions';

type Options = { mode: Modes } | undefined;

const withPayWall =
  (options: Options) =>
  (Component) =>
  ({ ...props }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user.user);
    const { getPlanType } = usePlan();
    const userPlan = getPlanType(user);

    const mode: Modes = options ? options.mode : null;

    const startTrial = () => {
      dispatch({ type: START_TRIAL });
      if (action) action();
    };

    if (mode && mode === 'fullComponent')
      return (
        <FullComponentPayWall
          props={props}
          userPlan={userPlan}
          component={Component}
          startTrial={startTrial}
        />
      );
    return (
      <OnlyFunctionsPayWall
        props={props}
        userPlan={userPlan}
        component={Component}
        startTrial={startTrial}
      />
    );
  };

const FullComponentPayWall = ({
  userPlan,
  component: Component,
  props,
  startTrial,
}) => {
  const [isRedirect, setRedirect] = useState(false);
  const [isModal, setModal] = useState(true);

  const onCancel = () => {
    setRedirect(true);
    setModal(false);
  };

  if (isRedirect) return <Redirect to="/" />;

  if (isModal && userPlan === PLAN.participant)
    return <StartTrialModal isOpen onCancel={onCancel} onSubmit={startTrial} />;

  if (isModal && userPlan === PLAN.expired)
    return <TrialExpiredModal isOpen onCancel={onCancel} onSubmit={emptyF} />;

  if (userPlan) return <Component {...props} />;
  return <LoadingAndPulse size={20} color="#3D9AE2" />;
};

const OnlyFunctionsPayWall = ({
  userPlan,
  component: Component,
  props,
  startTrial,
}) => {
  const [displayedModal, setDisplayedModal] = useState(null);
  const [, setAction] = callOnce();
  const onCancel = () => setDisplayedModal(null);

  const isActionCalled = () => {
    const validTrial = userPlan === PLAN.trial && userPlan !== PLAN.expired;
    return (
      validTrial ||
      userPlan === PLAN.enterprise ||
      userPlan === PLAN.single_creator ||
      userPlan === PLAN.business_monthly ||
      userPlan === PLAN.premium_monthly
    );
  };

  const modals = {
    trial: <StartTrialModal isOpen onCancel={onCancel} onSubmit={startTrial} />,
    expired: <TrialExpiredModal isOpen onCancel={onCancel} onSubmit={emptyF} />,
  };

  const usePayWall = () => (actionIn) => {
    const isAction = isActionCalled();
    if (isAction) return actionIn;
    setAction(actionIn);

    if (userPlan === PLAN.expired)
      return () => setDisplayedModal(modals.expired);

    if (userPlan === PLAN.participant || userPlan === PLAN.guest)
      return () => setDisplayedModal(modals.trial);

    return emptyF;
  };

  return (
    <>
      <Component usePayWall={usePayWall} {...props} />
      {displayedModal}
    </>
  );
};

let action = null;

const emptyF = () => {};

const callOnce = () => {
  const callAndDispose = (...args) => {
    if (typeof action === 'function') action(...args);
    action = null;
  };

  const setAction = (newAction) => {
    action = newAction;
  };

  return [callAndDispose, setAction];
};

export { withPayWall };
